import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useTextReveal — Smoothly reveals text character-by-character.
 *
 * Instead of rendering SSE chunks instantly (jerky), this buffers
 * incoming text and reveals it at a constant rate for a smooth typewriter effect.
 *
 * @param {number} charsPerFrame - Characters to reveal per animation frame (~16ms). Default 3.
 * @returns {{ revealedText, pushText, finish, reset }}
 */
export function useTextReveal(charsPerFrame = 3) {
  const [revealedText, setRevealedText] = useState('');
  const bufferRef = useRef('');      // Full text received so far
  const revealedRef = useRef(0);     // How many chars have been revealed
  const rafRef = useRef(null);       // requestAnimationFrame ID

  const tick = useCallback(() => {
    const buffer = bufferRef.current;
    const current = revealedRef.current;

    if (current < buffer.length) {
      // Reveal next chunk of characters
      const next = Math.min(current + charsPerFrame, buffer.length);
      revealedRef.current = next;
      setRevealedText(buffer.slice(0, next));
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rafRef.current = null;
    }
  }, [charsPerFrame]);

  const pushText = useCallback((newText) => {
    bufferRef.current += newText;
    // Start animation if not already running
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  const finish = useCallback(() => {
    // Instantly reveal all remaining text
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    revealedRef.current = bufferRef.current.length;
    setRevealedText(bufferRef.current);
    return bufferRef.current;
  }, []);

  const reset = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    bufferRef.current = '';
    revealedRef.current = 0;
    setRevealedText('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { revealedText, pushText, finish, reset };
}
